import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import GlobalSidebarItem from '../components/sidebar/GlobalSidebar/GlobalSidebarItem';
import { ArrowLeftIcon, CheckIcon } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { usePackStorage } from '../Layout/usePackStorage';
import { AppLayout } from '../Layout/AppLayout';
import LectureSessions from './components/LectureSessions';

export default function Lectures() {
  const { purchasedPacks, selectedPack, handlePackChange } = usePackStorage();
  const navigate = useNavigate();
  const { packId } = useParams();
  const [searchParams] = useSearchParams();

  const [lectures, setLectures] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);

  const user = JSON.parse(localStorage.getItem('userProfile'));

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await axios.get(`/api/packs/${packId}/lectures`);
        const list = res?.data?.data || [];
        setLectures(list);

        const lectureTitleFromURL = searchParams.get('tab');
        const matched = list.find((item) => item?.title === lectureTitleFromURL);
        setSelectedTab(matched?.title || list?.[0]?.title || null);
      } catch (error) {
        navigate('/v2/home');
      }
    }

    if (packId) fetchData();
  }, [packId]);

  const tabDescriptionMapping = useMemo(() => {
    const mapping = {};
    (lectures || []).forEach((item) => {
      mapping[item?.title] = item?.description;
    });
    return mapping;
  }, [lectures]);

  useEffect(() => {
    if (!selectedTab) return;
    window.webengage?.track?.('Viewed Lectures', {
      'User Id': user?.email,
      'Pack Id': packId,
      Lecture: selectedTab,
    });
  }, [selectedTab]);

  const handleTabChange = (tab) => {
    setSelectedTab(tab?.title);
  };

  const selectedLecture = useMemo(() => {
    return (lectures || []).find((l) => l?.title === selectedTab) || null;
  }, [lectures, selectedTab]);

  if (!lectures) return null;

  return (
    <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack} onPackChange={handlePackChange}>
      <div className="flex flex-row max-w-7xl mx-auto flex-1 m-0 p-0 overflow-hidden">
        {/* Left Sidebar */}
        <div className="hidden lg:flex flex-col w-[264px] items-start pl-6">
          <div className="text-[var(--q3-neutral-default)] text-2xl font-bold my-2 px-4 py-6">Lectures</div>
          <div className="w-full overflow-y-auto pr-2">
            {(lectures || []).map((tab, index) => (
              <GlobalSidebarItem
                key={tab?._id || index}
                icon={tab?.icon}
                title={tab?.title}
                isSelected={tab?.title === selectedTab}
                onClick={() => handleTabChange(tab)}
                className="w-full"
              />
            ))}
          </div>
        </div>

        {/* Mobile bottom tabs */}
        <div className="lg:hidden q3-glass fixed border-t bottom-0 z-20 bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] w-full py-3 px-2">
          <div className="flex flex-row gap-[11px] overflow-x-auto">
            {(lectures || []).map((tab) => (
              <Button
                variant={'outline'}
                className={`rounded-full ${selectedTab === tab?.title ? '!bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)] hover:!text-[var(--q3-neutral-default_inverted)] hover:!bg-[var(--q3-neutral-default)]' : 'hover:bg-[var(--q3-surface-dimmest)]'}`}
                onClick={() => handleTabChange(tab)}
                key={`tab-${tab?._id || tab?.title}`}
              >
                {tab?.title}
                {selectedTab === tab?.title ? <CheckIcon /> : null}
              </Button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col w-full overflow-y-auto">
          <div className="sticky top-0 z-10 flex flex-col lg:flex-row justify-between p-6 pb-4 q3-glass rounded">
            <div
              className="block lg:hidden bg-[var(--q3-surface-dim)] rounded-full w-fit p-2 mb-2 cursor-pointer"
              onClick={() => navigate('/v2/home')}
            >
              <ArrowLeftIcon size={24} className="text-[var(--q3-neutral-default)]" />
            </div>

            <div className="flex gap-2 flex-col items-start">
              <div>
                <p className="text-2xl text-[var(--q3-neutral-default)] font-bold">{selectedTab}</p>
                {selectedTab ? (
                  <p className="text-sm text-[var(--q3-neutral-light)]">{tabDescriptionMapping[selectedTab]}</p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="p-6 pt-4 pb-24 lg:pb-6">
            <LectureSessions
              packId={packId}
              lectureTitle={selectedLecture?.title}
              sessions={selectedLecture?.sessions || []}
            />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
